import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';

import { dotFormatter, formatDate, setTranslationValues } from '../../../../../helpers';
import { useOutsideClick, useStore, useTranslation } from '../../../../../hooks';
import { PGWheelInfo } from '../../../../../models';
import { PGWheelPopupTimer } from '../popup-timer';
import { T } from '../../../../../constants';

import WheelPopup from '../../../../../assets/images/wheel/wheel-popup.png';
import WheelBG from '../../../../../assets/images/wheel/wheel-bg.png';

interface PGWheelDetailPopupProps {
  wheelInfo: PGWheelInfo;
}

export const PGWheelDetailPopup: React.FC<PGWheelDetailPopupProps> = observer(({ wheelInfo }) => {
  const { boostedJackpotStore } = useStore();
  const { lastWinAmount, largestWinAmount } = boostedJackpotStore;

  const {
    lastWinDate,
    largestWinDate,
    totalWinCount,
    remainingWinCount,
    currencyCode,
    poolAmount,
    remainingPoolAmount,
    endDate,
    minBetAmount
  } = wheelInfo;
  const t = useTranslation();

  const handleClose = useCallback(() => {
    boostedJackpotStore.setIsBoostedJackpotInfoPopup(false);
  }, [boostedJackpotStore]);

  const { modalRef, containerRef } = useOutsideClick(handleClose);

  const rulesContent = useMemo(() => {
    const content = setTranslationValues(t(T.PG_WHEEL_RICH_RULES), { minBetAmount: `${minBetAmount} ${currencyCode}` });
    return { __html: content };
  }, [t, minBetAmount, currencyCode]);

  return (
    <div className="bj-info-wrp" ref={containerRef}>
      <div className="bj-info" ref={modalRef}>
        <i className="icon-close close-icon" onClick={handleClose}></i>
        <div className="bj-popup-content">
          <div className="bj-info-header">
            <div className="bj-info-header-corner">
              <p className="bj-info-header-corner-title">{t(T.WINNING_COUNT)}</p>
              <p className="bj-header-total">{totalWinCount}</p>
            </div>
            <img src={WheelBG} alt="wheel" className="bj-info-header-img" />
            <div className="bj-info-header-corner">
              <p className="bj-info-header-corner-title">{t(T.WINNERS_COUNT_LEFT)}</p>
              <p className="bj-header-total">{remainingWinCount}</p>
            </div>
          </div>
          <div className="bj-info-prize">
            <p className="bj-info-prize-title">{t(T.PRIZE_POOL)}</p>
            <p className="bj-info-prize-text">{dotFormatter(poolAmount)} <span>{currencyCode}</span></p>
          </div>
          <div className="bj-info-prize">
            <p className="bj-info-prize-title">{t(T.PRIZE_POOL_LEFT)}</p>
            <div className="bj-info-prize-block">
              <p className="bj-info-header-corner-subtitle">{dotFormatter(remainingPoolAmount)} {currencyCode}</p>
            </div>
          </div>
          <div className="bj-info-prize-content">
            {
              !!lastWinAmount && (
                <div className="bj-info-prize">
                  <p className="bj-info-prize-title">{t(T.LAST_WINNER)}</p>
                  <div className="bj-info-prize-block">
                    <p className="bj-info-header-corner-subtitle">{dotFormatter(lastWinAmount)} {currencyCode}</p>
                    <p className="bj-info-header-corner-date">{lastWinDate ? formatDate(new Date(lastWinDate)) : ''}</p>
                  </div>
                </div>
              )
            }
            {
              !!largestWinAmount && (
                <div className="bj-info-prize">
                  <p className="bj-info-prize-title">{t(T.TOP_WINNER)}</p>
                  <div className="bj-info-prize-block">
                    <p className="bj-info-header-corner-subtitle">{dotFormatter(largestWinAmount)} {currencyCode}</p>
                    <p className="bj-info-header-corner-date">{largestWinDate ? formatDate(new Date(largestWinDate)) : ''}</p>
                  </div>
                </div>
              )
            }
          </div>
          <div className="bj-info-countdown">
            <p className="bj-info-countdown-title">{t(T.ENDS_IN)}</p>
            <PGWheelPopupTimer date={endDate} />
          </div>
          <p className="bj-info-rules-title">{t(T.RULES)}</p>
          <div
            dangerouslySetInnerHTML={rulesContent}
            className="bj-info-rules-content-group"
          />
          <div className="bj-info-state-content">
            <div className="wheel-glow-wrapper">
              <img src={WheelPopup} alt="mini wheel" className="bj-info-state-small" />
            </div>
            <div className="wheel-glow-wrapper active">
              <img src={WheelPopup} alt="mini wheel" className="bj-info-state-small" />
            </div>
          </div>
          <p className="bj-info-rules-content-desc">
            {t(T.PG_WHEEL_RULES_BOTTOM)}
          </p>
        </div>
      </div>
    </div>
  );
});
